import {
  BlockWrapper,
  defineBlock,
  smartProps,
  useEditor,
  ChildrenBlock,
  useInspectorControl,
} from "@smartfire/js-block-engine";
import style from "./block.module.scss";
import { InnerBlocks } from "@wordpress/block-editor";
import { lazy, useEffect, useRef, useState, useCallback } from "react";
import clsx from "clsx";
import MiniVideo from "./components/mini-video";

import { ReactComponent as TriangleIcon } from "../assets/icons/triangle.svg";
import SliderAllVideos from "./components/slider-all-videos";

const InspectorLazy = lazy(() => import("./Inspector"));

function Block({
  videoId,
  link,
  fullWidth,
  videoType,
  videos,
  image,
  sliderAllVideos,
}) {
  useInspectorControl(InspectorLazy, []);
  const { isEditing } = useEditor();

  const playerRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const initializePlayer = useCallback((videoId) => {
    if (playerRef.current && window.YT && window.YT.Player) {
      new window.YT.Player(playerRef.current, {
        videoId: videoId,
        height: "100%",
        width: "100%",
        playerVars: {
          enablejsapi: 1,
          playlist: videoId,
          disablekb: 1,
          showinfo: 0,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          fs: 0,
          iv_load_policy: 3,
        },
        events: {
          onReady: (event) => {
            setPlayer(event.target);
          },
          onError: (error) => {
            console.error("YouTube Player Error:", error);
          },
        },
      });
    }
  }, []);

  useEffect(() => {
    let script = document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]'
    );
    if (!script) {
      script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      document.body.appendChild(script);
    }

    // Ajoutez votre callback à la liste
    window.youtubeIframeAPIReadyCallbacks =
      window.youtubeIframeAPIReadyCallbacks || [];
    window.youtubeIframeAPIReadyCallbacks.push(() => {
      const initialVideoId =
        videoType === "multiple" && !isEditing && videos && videos.length > 0
          ? videos[0]?.video_id
          : videoId;
      initializePlayer(initialVideoId);
    });

    // Assurez-vous que la fonction onYouTubeIframeAPIReady existe
    if (!window.onYouTubeIframeAPIReady) {
      window.onYouTubeIframeAPIReady = () => {
        window.youtubeIframeAPIReadyCallbacks.forEach((callback) => {
          try {
            callback();
          } catch (error) {
            console.error("Error in YouTube API Ready Callback:", error);
          }
        });
      };
    }

    return () => {
      script.remove();
      if (player) {
        player.destroy();
        setPlayer(null);
      }
    };
  }, [initializePlayer, videoId, videoType, isEditing, videos, player]);

  useEffect(() => {
    if (player && videoType === "multiple" && !isEditing) {
      const newVideoId = videos[selectedIndex]?.video_id;
      if (newVideoId) {
        try {
          player.cueVideoById(newVideoId);
          setIsPlaying(false);
        } catch (e) {
          console.error("Error cueing video:", e);
        }
      } else {
        console.warn("Invalid video ID or player not ready");
      }
    }
  }, [player, selectedIndex, videoType, isEditing, videos]);

  const handlePlay = useCallback(() => {
    if (player && !isEditing) {
      player.playVideo();
      setIsPlaying(true);
    }
  }, [player, isEditing]);

  const handlePause = useCallback(() => {
    if (player && !isEditing) {
      player.pauseVideo();
      setIsPlaying(false);
    }
  }, [player, isEditing]);

  const handleToggle = () => {
    isPlaying ? handlePause() : handlePlay();
  };

  const handleClickVideo = (videoIndex) => {
    setSelectedIndex(videoIndex);
    initializePlayer(videoIndex);
    setIsPlaying(false);
  };

  if (isEditing && videoType === "multiple") {
    return <p>Aucune prévisualisation est disponible</p>;
  }

  return videoId || (videos && videos.length > 0) ? (
    <BlockWrapper.Div>
      <div
        className={clsx(
          style.block,
          !fullWidth && style.notFullWidth,
          sliderAllVideos && style.hasSlider
        )}
      >
        <div ref={playerRef} id="new-video-player"></div>
        <div
          className={clsx(style.contentDiv, isPlaying && style.isPlaying)}
          onClick={handleToggle}
          style={{
            background: `linear-gradient(180deg, rgba(0, 0, 0, 0.00)0%, rgba(0, 0, 0, 0.60)100%), center/cover no-repeat url(${
              image && image.url
            })`,
          }}
        >
          {videoType === "unique" ? (
            <div>
              {isEditing ? (
                <InnerBlocks
                  allowedBlocks={["core/paragraph", "core/heading"]}
                  template={[
                    [
                      "core/heading",
                      {
                        content: "AEON présentation",
                        placeholder: "My placeholder",
                      },
                    ],
                    [
                      "core/paragraph",
                      {
                        content:
                          "Lorem ipsum dolor sit amet consectetur. Tincidunt eu faucibus a pharetra pretium amet consequat.",
                        placeholder: "Renseigner du texte...",
                      },
                    ],
                  ]}
                />
              ) : (
                <ChildrenBlock />
              )}
              <div className={style.buttonGroup}>
                <button onClick={handlePlay}>
                  <TriangleIcon />
                  Lire la vidéo
                </button>
                <a href={link}>En savoir plus</a>
              </div>
            </div>
          ) : (
            <div>
              {videos[selectedIndex] && (
                <>
                  <h2>{videos[selectedIndex]?.title}</h2>
                  <div
                    className="post__content"
                    dangerouslySetInnerHTML={{
                      __html: videos[selectedIndex]?.content || "",
                    }}
                  ></div>

                  <div className={style.buttonGroup}>
                    <button onClick={handlePlay}>
                      <TriangleIcon />
                      Lire la vidéo
                    </button>
                    <a href={videos[selectedIndex]?.link}>En savoir plus</a>
                  </div>
                  <div className={style.miniVideoDiv}>
                    {videos
                      .slice(selectedIndex + 1, selectedIndex + 4)
                      .map((video, i) => (
                        <MiniVideo
                          key={i}
                          video={video}
                          handleClick={() => {
                            handleClickVideo(selectedIndex + 1 + i);
                          }}
                        />
                      ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {sliderAllVideos && videoType === "multiple" && (
        <SliderAllVideos videos={videos} setSelectedIndex={setSelectedIndex} />
      )}
    </BlockWrapper.Div>
  ) : (
    <p>Rien</p>
  );
}

export default defineBlock({
  Block,
  smartProps: {
    videoId: smartProps.string.withDefault(""),
    link: smartProps.string.withDefault(""),
    fullWidth: smartProps.bool.withDefault(true),
    videoType: smartProps.string.withDefault("unique"),
    videoPost: smartProps.string.withDefault(""),
    image: smartProps.image,
    sliderAllVideos: smartProps.bool.withDefault(false),
  },
  setup: {
    title: "New Video Player",
  },
});
